import { Checkbox, FormControlLabel } from "@mui/material";
import { DatePicker as MUIDatePicker } from "@mui/x-date-pickers";
import React, { useEffect, useState } from "react";

import { fields } from "../data/fields";
import type { FieldName } from "../data/fields";

interface DatePickerProps {
  addSearchField: (
    field: FieldName | "endDate",
    value: string | undefined,
  ) => void;
  deleteSearchFields: (fields: FieldName[]) => void;
  useRange?: boolean;
  selectedDate?: Date | null;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  addSearchField,
  deleteSearchFields,
  useRange = false,
  selectedDate: propSelectedDate,
}) => {
  const [showDateRange, setShowDateRange] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<{
    start: Date | null;
    end: Date | null;
  }>({ start: null, end: null });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (propSelectedDate !== undefined) {
      setSelectedDate((prev) => ({ ...prev, start: propSelectedDate }));
    }
  }, [propSelectedDate]);

  const handleDateChange = (date: Date | null, boundary: "start" | "end") => {
    // Validate the date
    if (date && isNaN(date.getTime())) {
      // Invalid date
      return;
    }

    // Update local state first
    const newDateState = { ...selectedDate, [boundary]: date };
    setSelectedDate(newDateState);

    const field = boundary === "start" ? fields.created.fieldName : "endDate";
    if (date) {
      // Set time to start of day (00:00:00) for start date and end of day (23:59:59) for end date
      const dateWithTime = new Date(date);
      if (boundary === "start") {
        dateWithTime.setHours(0, 0, 0, 0);
      } else {
        dateWithTime.setHours(23, 59, 59, 999);
      }
      addSearchField(field, dateWithTime.toISOString());
    } else {
      deleteSearchFields([fields.created.fieldName]);
    }
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <MUIDatePicker
          // TODO - make this width a global style
          sx={{ width: (theme) => theme.spacing(35) }}
          maxDate={selectedDate.end !== null ? selectedDate.end : undefined}
          label={showDateRange ? "Start date" : "Created"}
          value={selectedDate.start}
          onChange={(date) => handleDateChange(date, "start")}
          slotProps={{
            actionBar: { actions: ["today", "clear"] },
            field: {
              clearable: true,
              onClear: () => handleDateChange(null, "start"),
            },
            textField: {
              InputLabelProps: { shrink: true, sx: { fontSize: "1rem" } },
              onClick: () => setIsOpen(true),
            },
          }}
          slots={{
            openPickerButton: () => null,
          }}
          open={isOpen}
          onClose={() => setIsOpen(false)}
        />
        {showDateRange && useRange && (
          <FormControlLabel
            control={
              <Checkbox
                checked={showDateRange}
                onChange={() => {
                  setShowDateRange(!showDateRange);
                }}
              />
            }
            label="Filter by date range"
            sx={{
              "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
              marginLeft: 0, // Remove default left margin
            }}
          />
        )}
      </div>
      {showDateRange && useRange && (
        <MUIDatePicker
          label="End date"
          value={selectedDate.end || selectedDate.start}
          onChange={(date) => handleDateChange(date, "end")}
          minDate={selectedDate.start !== null ? selectedDate.start : undefined}
          slotProps={{
            actionBar: { actions: ["today", "clear"] },
            field: {
              clearable: true,
              onClear: () => handleDateChange(null, "end"),
            },
          }}
        />
      )}
    </>
  );
};
